import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Users, 
  Target, 
  BarChart3, 
  Briefcase,
  Layers,
  GitBranch,
  Award,
  Building2
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const coreOfferings = [
  {
    icon: Briefcase,
    title: 'Executive Mentorship & Advisory',
    description: 'High-level, bespoke advisory for Ministers, Directors General, and Agency Heads. We provide a confidential sounding board for navigating complex systemic transitions, political economy challenges, and the implementation of the HSL Impact Model.'
  },
  {
    icon: Building2,
    title: 'Governance Architecture',
    description: 'We re-engineer the structural DNA of health ministries and NGOs.',
    subItems: [
      'Silo Elimination: Shifting organizations from fragmented units to integrated, agile systems.',
      'Decision-Intelligence: Designing governance structures that foster rapid, evidence-based decision-making.',
      'Accountability Design: Building transparent frameworks where performance is tied to actual health outcomes.'
    ]
  },
  {
    icon: GitBranch,
    title: 'Strategic Change Management',
    description: 'Infrastructure and technology are only as effective as their adoption. We provide strategic support for organizations undergoing major transformations.',
    subItems: [
      'Transitioning from paper-based to digital-first governance (Systemic Leapfrogging).',
      'Implementing Performance-Based Financing models.',
      'Merging health security protocols with primary healthcare delivery.'
    ]
  }
];

export const LeadershipPage = () => {
  return (
    <div data-testid="leadership-page">
      {/* Hero Section */}
      <section className="section-padding pt-32" data-testid="leadership-hero">
        <div className="container-main">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <motion.span 
              variants={itemVariants}
              className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-6 block"
            >
              Strategic Health Leadership
            </motion.span>
            
            <motion.h1 
              variants={itemVariants}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6"
            >
              Strengthening Cognitive Infrastructure: The Human Architecture of Health
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-lg md:text-xl text-slate-600 leading-relaxed"
            >
              A health system is only as resilient as the minds that govern it. Technical 
              innovations and financial investments often fail when they are layered upon 
              a foundation of "Accidental Leadership." At HSL, we specialize in 
              the human architecture of the health sector, moving beyond generic management 
              training to offer <span className="text-teal-600 font-semibold">Leadership by Design</span>.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Ambidextrous Leader Program */}
      <section className="section-padding bg-slate-50" data-testid="ambidextrous-program">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              Flagship Program
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-slate-900 mb-6">
              The Ambidextrous Leader Program
            </h2>
            <p className="text-slate-600 text-lg">
              Modern health leadership requires the ability to thrive in two simultaneous time 
              horizons. Our flagship program develops executives who are both master operators 
              and visionary architects.
            </p>
          </motion.div>

          {/* Two Columns - Operator & Architect */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* The Operator */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white border border-slate-200 p-8 lg:p-10 hover:border-slate-300 transition-colors"
              data-testid="leader-operator"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-slate-100 border border-slate-300 flex items-center justify-center">
                  <BarChart3 size={32} className="text-slate-500" />
                </div>
                <div>
                  <span className="font-mono text-xs uppercase tracking-widest text-slate-500 block">
                    Exploitation
                  </span>
                  <h3 className="font-heading text-2xl text-slate-900">The Operator</h3>
                </div>
              </div>
              
              <p className="text-slate-600 mb-6 leading-relaxed">
                We equip leaders with the analytical tools to master operational efficiency. 
                This focuses on maximizing the <span className="text-teal-600 font-semibold">Marginal 
                Utility</span> of current health budgets, optimizing supply chains, and refining 
                frontline service delivery through data-driven oversight.
              </p>

              <ul className="space-y-3">
                {['Budget Optimization', 'Supply Chain Excellence', 'Data-Driven Oversight'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-500">
                    <div className="w-1.5 h-1.5 bg-slate-600 rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* The Architect */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white border border-teal-200 p-8 lg:p-10 hover:border-teal-500 transition-colors"
              data-testid="leader-architect"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-teal-100 border border-teal-200 flex items-center justify-center">
                  <Target size={32} className="text-teal-600" />
                </div>
                <div>
                  <span className="font-mono text-xs uppercase tracking-widest text-teal-600 block">
                    Exploration
                  </span>
                  <h3 className="font-heading text-2xl text-slate-900">The Architect</h3>
                </div>
              </div>
              
              <p className="text-slate-600 mb-6 leading-relaxed">
                We train leaders to design the future. This involves architecting future-proof 
                biosecurity frameworks, navigating the ethical integration of AI, and scaling 
                digital health equity to reach the <span className="text-teal-600 font-semibold">"Unserved 
                Frontiers"</span> of their population.
              </p>

              <ul className="space-y-3">
                {['Biosecurity Architecture', 'AI Integration Strategy', 'Digital Health Equity'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-600">
                    <div className="w-1.5 h-1.5 bg-teal-400 rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Offerings */}
      <section className="section-padding" data-testid="core-offerings">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              Core Offerings
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-slate-900 mb-6">
              Architecting Change
            </h2>
          </motion.div>

          <div className="space-y-6">
            {coreOfferings.map((offering, index) => (
              <motion.div
                key={offering.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-50 border border-slate-200 p-8 hover:border-slate-300 transition-colors"
                data-testid={`offering-${index}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="w-14 h-14 bg-slate-100 border border-slate-300 flex items-center justify-center flex-shrink-0">
                    <offering.icon size={28} className="text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl text-slate-900 mb-3">
                      {index + 1}. {offering.title}
                    </h3>
                    <p className="text-slate-500 leading-relaxed mb-4">{offering.description}</p>
                    
                    {offering.subItems && (
                      <ul className="space-y-2 pl-4 border-l border-slate-300">
                        {offering.subItems.map((item, i) => (
                          <li key={i} className="text-slate-500 text-sm">
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HSL Advantage */}
      <section className="section-padding bg-slate-50" data-testid="hsl-advantage">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden bg-white border border-slate-200 p-10 lg:p-16"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            
            <div className="relative grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-4 block">
                  The HSL Advantage
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  Complexity-Ready Leadership
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Using advanced analytical methods, we identify the 
                  specific leadership "causal recipes" that drive success in deep local contexts. 
                  We don't teach theory; we provide the blueprint for designed excellence.
                </p>
                <Link
                  to="/leadership-academy/enroll"
                  className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-300 font-medium transition-colors group"
                  data-testid="advantage-cta"
                >
                  Enroll in the Leadership Academy
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-100/50 p-6 text-center">
                  <Award size={32} className="text-teal-600 mx-auto mb-3" />
                  <span className="text-2xl font-heading text-slate-900 block mb-1">Executive</span>
                  <span className="text-slate-500 text-sm">Level Training</span>
                </div>
                <div className="bg-slate-100/50 p-6 text-center">
                  <Users size={32} className="text-teal-600 mx-auto mb-3" />
                  <span className="text-2xl font-heading text-slate-900 block mb-1">Pan-African</span>
                  <span className="text-slate-500 text-sm">Focus</span>
                </div>
                <div className="bg-slate-100/50 p-6 text-center">
                  <Building2 size={32} className="text-teal-600 mx-auto mb-3" />
                  <span className="text-2xl font-heading text-slate-900 block mb-1">Multilateral</span>
                  <span className="text-slate-500 text-sm">Partnerships</span>
                </div>
                <div className="bg-slate-100/50 p-6 text-center">
                  <Layers size={32} className="text-teal-600 mx-auto mb-3" />
                  <span className="text-2xl font-heading text-slate-900 block mb-1">Evidence</span>
                  <span className="text-slate-500 text-sm">Based Approach</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" data-testid="leadership-cta">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Ready to Transform Your Leadership?
            </h2>
            <p className="text-slate-500 text-lg mb-8 max-w-2xl mx-auto">
              Join the HSL Leadership Academy and become an Ambidextrous Leader.
            </p>
            <Link
              to="/leadership-academy/enroll"
              className="btn-primary inline-flex items-center gap-2 group"
              data-testid="leadership-final-cta"
            >
              Start Your Journey
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
